import { useCallback } from 'react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import type { RequirementNode, RequirementType, RequirementTheme } from '@ispot/shared'
import { RequirementRow } from './RequirementRow'
import { QuickAddRow } from './QuickAddRow'
import { gridTemplate, COLUMN_HEADERS } from './grid'
import type { PersonaLookupEntry } from './usePersonaLookup'

interface ActiveFilters {
  type?: string
  priority?: string
  status?: string
  themeIds?: string[]
  personaIds?: string[]
}

interface RequirementsTableProps {
  tree: RequirementNode[]
  themeById?: Map<string, RequirementTheme>
  visibleColumns: Record<string, boolean>
  expandedIds: Set<string>
  selectedIds: Set<string>
  filterQuery: string
  activeFilters?: ActiveFilters
  density?: 'compact' | 'comfortable'
  onToggleExpand: (id: string) => void
  onToggleSelect: (id: string) => void
  onUpdate: (id: string, data: Partial<RequirementNode>) => Promise<unknown>
  onDelete: (id: string) => Promise<void>
  onAddChild: (parentId: string) => void
  onCreate: (data: { name: string; type: RequirementType; parentId?: string }) => Promise<unknown>
  onGenerateChildren: (parentId: string) => void
  onReorder: (parentId: string | null, orderedIds: string[]) => void
  detailId: string | null
  onToggleDetail: (id: string) => void
  resolvePersona?: (name: string | undefined) => PersonaLookupEntry | null
}

/**
 * Inherited matching: a node passes theme/persona filters if its own tags
 * match OR any ancestor's tags match. Text/type/priority/status match the
 * node itself. Children still pass through (whole branch visible if any
 * ancestor matches).
 */
function matchesFilter(
  node: RequirementNode,
  query: string,
  filters: ActiveFilters | undefined,
  inheritedThemes: Set<string>,
  inheritedPersonas: Set<string>,
): boolean {
  const effectiveThemes = new Set([...inheritedThemes, ...node.themeIds])
  const effectivePersonas = new Set([...inheritedPersonas, ...node.personaIds])

  const hasAnyFilter = !!query || !!filters?.type || !!filters?.priority || !!filters?.status
    || (filters?.themeIds?.length ?? 0) > 0 || (filters?.personaIds?.length ?? 0) > 0
  if (!hasAnyFilter) return true

  const q = query.toLowerCase()
  const textOk = !query || node.name.toLowerCase().includes(q) || (node.description?.toLowerCase().includes(q) ?? false)
  const typeOk = !filters?.type || node.type === filters.type
  const priorityOk = !filters?.priority || node.priority === filters.priority
  const statusOk = !filters?.status || node.status === filters.status
  const themeOk = !filters?.themeIds?.length
    || filters.themeIds.some(id => effectiveThemes.has(id))
  const personaOk = !filters?.personaIds?.length
    || filters.personaIds.some(id => effectivePersonas.has(id))

  if (textOk && typeOk && priorityOk && statusOk && themeOk && personaOk) return true
  return node.children.some(c => matchesFilter(c, query, filters, effectiveThemes, effectivePersonas))
}

function collectVisibleIds(
  nodes: RequirementNode[],
  expandedIds: Set<string>,
  filterQuery: string,
  activeFilters: ActiveFilters | undefined,
  inheritedThemes: Set<string>,
  inheritedPersonas: Set<string>,
): string[] {
  const ids: string[] = []
  for (const node of nodes) {
    if (!matchesFilter(node, filterQuery, activeFilters, inheritedThemes, inheritedPersonas)) continue
    ids.push(node.id)
    if (expandedIds.has(node.id) && node.children.length > 0) {
      const childThemes = new Set([...inheritedThemes, ...node.themeIds])
      const childPersonas = new Set([...inheritedPersonas, ...node.personaIds])
      ids.push(...collectVisibleIds(node.children, expandedIds, filterQuery, activeFilters, childThemes, childPersonas))
    }
  }
  return ids
}

function renderRows(
  nodes: RequirementNode[],
  props: Omit<RequirementsTableProps, 'tree' | 'filterQuery' | 'activeFilters' | 'onReorder'>,
  filterQuery: string,
  activeFilters: ActiveFilters | undefined,
  inheritedThemes: Set<string>,
  inheritedPersonas: Set<string>,
): React.ReactNode[] {
  const rows: React.ReactNode[] = []

  for (const node of nodes) {
    if (!matchesFilter(node, filterQuery, activeFilters, inheritedThemes, inheritedPersonas)) continue

    rows.push(
      <RequirementRow
        key={node.id}
        node={node}
        themeById={props.themeById}
        visibleColumns={props.visibleColumns}
        isExpanded={props.expandedIds.has(node.id)}
        isSelected={props.selectedIds.has(node.id)}
        isDetailOpen={props.detailId === node.id}
        onToggleExpand={props.onToggleExpand}
        onToggleSelect={props.onToggleSelect}
        onUpdate={props.onUpdate}
        onDelete={props.onDelete}
        onAddChild={props.onAddChild}
        onGenerateChildren={props.onGenerateChildren}
        onToggleDetail={props.onToggleDetail}
        isDraggable={!filterQuery}
        density={props.density}
        resolvePersona={props.resolvePersona}
      />,
    )

    if (props.expandedIds.has(node.id)) {
      if (node.children.length > 0) {
        const childThemes = new Set([...inheritedThemes, ...node.themeIds])
        const childPersonas = new Set([...inheritedPersonas, ...node.personaIds])
        rows.push(...renderRows(node.children, props, filterQuery, activeFilters, childThemes, childPersonas))
      }
      if (node.depth < 3 && !filterQuery) {
        rows.push(
          <QuickAddRow
            key={`add-${node.id}`}
            parentId={node.id}
            depth={node.depth + 1}
            visibleColumns={props.visibleColumns}
            onCreate={props.onCreate}
          />,
        )
      }
    }
  }

  return rows
}

function HeaderRow({ visibleColumns }: { visibleColumns: Record<string, boolean> }) {
  const headerStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--color-fg-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    padding: '10px 0',
  }
  return (
    <div
      role="row"
      style={{
        display: 'grid',
        gridTemplateColumns: gridTemplate(visibleColumns),
        alignItems: 'center',
        borderBottom: '1px solid var(--color-warm-200)',
        background: 'color-mix(in oklab, var(--color-warm-50) 80%, transparent)',
        position: 'sticky',
        top: 0,
        zIndex: 3,
        backdropFilter: 'blur(4px)',
      }}
    >
      <span aria-hidden />
      <span aria-hidden />
      <div style={{ ...headerStyle, paddingLeft: 4 }}>Requirement</div>
      {COLUMN_HEADERS.map(col =>
        visibleColumns[col.key] !== false ? (
          <div key={col.key} style={headerStyle}>{col.label}</div>
        ) : null,
      )}
    </div>
  )
}

export function RequirementsTable(props: RequirementsTableProps) {
  const { tree, themeById, visibleColumns, filterQuery, activeFilters, onReorder, density, resolvePersona, ...rest } = props

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  const emptySet = new Set<string>()
  const visibleIds = collectVisibleIds(tree, rest.expandedIds, filterQuery, activeFilters, emptySet, emptySet)

  const parentMap = new Map<string, string | null>()
  const siblingMap = new Map<string | null, string[]>()

  function buildMaps(nodes: RequirementNode[], parentId: string | null) {
    const ids = nodes.map(n => n.id)
    siblingMap.set(parentId, ids)
    for (const node of nodes) {
      parentMap.set(node.id, parentId)
      if (node.children.length > 0) buildMaps(node.children, node.id)
    }
  }
  buildMaps(tree, null)

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const activeId = String(active.id)
    const overId = String(over.id)

    if (!parentMap.has(activeId) || !parentMap.has(overId)) return
    const activeParent = parentMap.get(activeId) ?? null
    const overParent = parentMap.get(overId) ?? null
    if (activeParent !== overParent) return

    const siblings = siblingMap.get(activeParent) ?? []
    const oldIndex = siblings.indexOf(activeId)
    const newIndex = siblings.indexOf(overId)
    if (oldIndex === -1 || newIndex === -1) return

    const newOrder = arrayMove(siblings, oldIndex, newIndex)
    onReorder(activeParent ?? null, newOrder)
  }, [onReorder, parentMap, siblingMap])

  if (tree.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center py-20"
        style={{
          background: 'var(--color-bg-card)',
        }}
      >
        <p className="text-base font-semibold" style={{ color: 'var(--color-fg)', fontFamily: 'var(--font-display)' }}>
          No requirements yet
        </p>
        <p className="text-sm mt-1" style={{ color: 'var(--color-fg-muted)' }}>
          Add your first requirement or generate from research
        </p>
      </div>
    )
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={visibleIds} strategy={verticalListSortingStrategy}>
        <div
          role="table"
          style={{
            background: 'var(--color-bg-card)',
          }}
        >
          <HeaderRow visibleColumns={visibleColumns} />
          {renderRows(tree, { visibleColumns, density, resolvePersona, themeById, ...rest }, filterQuery, activeFilters, emptySet, emptySet)}
          {!filterQuery && (
            <QuickAddRow
              parentId={null}
              depth={0}
              visibleColumns={visibleColumns}
              onCreate={rest.onCreate}
            />
          )}
        </div>
      </SortableContext>
    </DndContext>
  )
}
